/**
 * 画像モーダル処理のカスタムフック
 * 画像クリック、ダウンロード機能を分離
 */

import { useState, useCallback } from 'react';
import { logger } from '../utils/logger';
import { ErrorHandler } from '../utils/errorHandler';

interface SelectedImage {
  url: string;
  name: string;
  index: number;
}

interface UseImageModalResult {
  selectedImage: SelectedImage | null;
  handleImageClick: (url: string, index: number, yokaiName?: string) => void;
  closeModal: () => void;
  downloadImage: () => Promise<void>;
  isDownloading: boolean;
}

export const useImageModal = (): UseImageModalResult => {
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleImageClick = useCallback((url: string, index: number, yokaiName: string = '妖怪') => {
    logger.userAction('画像クリック', { url, index, yokaiName });
    
    setSelectedImage({
      url,
      name: yokaiName,
      index: index + 1
    });
  }, []);

  const closeModal = useCallback(() => {
    logger.userAction('モーダル閉じる');
    setSelectedImage(null);
  }, []);

  const downloadImage = useCallback(async () => {
    if (!selectedImage) {
      logger.warn('DOWNLOAD', 'ダウンロード対象画像なし');
      return;
    }
    
    setIsDownloading(true);
    logger.userAction('画像ダウンロード開始', selectedImage);
    
    try {
      // 画像を取得
      const response = await fetch(selectedImage.url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      
      // ダウンロードリンクを作成
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedImage.name}_画像${selectedImage.index}.jpg`;
      
      // ダウンロードを実行
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // リソースをクリーンアップ
      window.URL.revokeObjectURL(url);
      
      logger.info('DOWNLOAD', '画像ダウンロード成功', {
        fileName: link.download,
        url: selectedImage.url
      });
      
    } catch (error) {
      logger.error('DOWNLOAD', '画像ダウンロードエラー', error);
      
      const appError = ErrorHandler.handleGenericError(error, '画像ダウンロード');
      alert(appError.userMessage);
      
    } finally {
      setIsDownloading(false);
    }
  }, [selectedImage]);

  return {
    selectedImage,
    handleImageClick,
    closeModal,
    downloadImage,
    isDownloading
  };
};